import { Color3, DynamicTexture, type Mesh, StandardMaterial } from "@babylonjs/core";
import { HERO_LIST, type HeroId, Role } from "@rivalwatch/shared";
import { renderHeroPortraits } from "../../game/portraits.ts";
import { fit, inkBust, mix, roundRect } from "../paint.ts";
import type { DressContext, Owned } from "../types.ts";

/** What tools/map/brussels.py writes on each MURAL_* / SIGN_* panel (glTF node extras). */
interface PanelExtras {
  w?: number;
  h?: number;
  style?: "street" | "lintel" | "deco" | "clock";
  text?: string;
  bg?: string;
  role?: "vanguard" | "duelist" | "strategist";
  /** The hero a mural features (a roster slug); any hero of `role` if it's not in the roster. */
  hero?: string;
  ground?: string;
  balloon?: string;
}

const INK = "#1a1c24";
const ENAMEL = "#24324a";
const PAPER = "#f4ecd6";
const ROLES: Record<string, Role> = { vanguard: Role.Tank, duelist: Role.Damage, strategist: Role.Support };
const FONTS = ["700 64px Cinzel", "700 64px 'Comic Neue'", "900 64px 'Big Shoulders Display'", "700 64px 'Big Shoulders Display'"];

type G = CanvasRenderingContext2D;

/** Pixels per metre: murals are seen from across a square, plaques from up close. */
const density = (mural: boolean, style?: string) => (mural ? 96 : style === "clock" ? 180 : 300);

/**
 * Paints the map's flat panels: enamel street plaques (French above Dutch), carved inscriptions,
 * the station's deco lettering, clock faces on real local time, and the comic-strip murals, each
 * a coloured panel with a caption, a speech balloon and a hero bust breaking out of the frame.
 */
export function paintPanels(ctx: DressContext): Owned[] {
  const out: Owned[] = [];
  let disposed = false;
  const jobs: (() => void)[] = [];
  const murals: { tex: DynamicTexture; ex: PanelExtras; hero: HeroId; W: number; H: number }[] = [];
  const portraits: Partial<Record<HeroId, HTMLCanvasElement>> = {};

  for (const mesh of ctx.panels) {
    const ex = ((mesh.metadata as { gltf?: { extras?: PanelExtras } } | null)?.gltf?.extras ?? {}) as PanelExtras;
    const mural = mesh.name.startsWith("MURAL_");
    const w = ex.w ?? 1;
    const h = ex.h ?? 1;
    const d = density(mural, ex.style);
    const W = Math.min(2048, Math.max(64, Math.round(w * d)));
    const H = Math.min(2048, Math.max(64, Math.round(h * d)));
    const tex = new DynamicTexture(`panel-${mesh.name}`, { width: W, height: H }, ctx.scene, true);
    tex.wrapU = tex.wrapV = DynamicTexture.CLAMP_ADDRESSMODE;
    const mat = new StandardMaterial(`panel-${mesh.name}`, ctx.scene);
    mat.diffuseTexture = tex;
    mat.specularColor = Color3.Black();
    mat.zOffset = -1;
    if (ex.style === "clock") {
      tex.hasAlpha = true;
      mat.useAlphaFromDiffuseTexture = true;
      mat.transparencyMode = StandardMaterial.MATERIAL_ALPHATEST;
    }
    (mesh as Mesh).material = mat;
    mesh.receiveShadows = true;
    out.push(tex, mat);
    const g = tex.getContext() as unknown as G;
    if (mural) {
      const heroes = HERO_LIST.filter((hd) => hd.role === ROLES[ex.role ?? "vanguard"]);
      let hash = 0;
      for (const ch of mesh.name) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
      const hero = (HERO_LIST.find((hd) => hd.slug === ex.hero) ?? heroes[hash % heroes.length]!).id;
      murals.push({ tex, ex, hero, W, H });
      jobs.push(() => {
        paintMural(g, W, H, ex, hero, portraits[hero]);
        tex.update(false);
      });
    } else if (ex.style === "clock") {
      const draw = () => {
        if (disposed) return;
        paintClock(g, W, H);
        tex.update(false);
      };
      jobs.push(draw);
      const timer = window.setInterval(draw, 30_000);
      out.push({ dispose: () => window.clearInterval(timer) });
    } else {
      jobs.push(() => {
        if (ex.style === "street") paintStreet(g, W, H, ex.text ?? "");
        else if (ex.style === "deco") paintDeco(g, W, H, ex.text ?? "", ex.bg ?? "#eadfc3");
        else paintLintel(g, W, H, ex.text ?? "", ex.bg ?? "#d9cba6");
        tex.update(false);
      });
    }
  }
  out.push({ dispose: () => void (disposed = true) });

  const paintAll = () => {
    if (!disposed) for (const job of jobs) job();
  };
  // Paint at once with whatever fonts are ready, again once the lettering faces arrive.
  paintAll();
  Promise.all(FONTS.map((f) => document.fonts.load(f)))
    .then(paintAll)
    .catch(() => undefined);
  // The murals' heroes are rendered from their real models once the engine is drawing frames.
  if (murals.length) {
    ctx.scene.onAfterRenderObservable.addOnce(() => {
      const ids = [...new Set(murals.map((m) => m.hero))];
      renderHeroPortraits(ctx.engine, 512, ids)
        .then(async (urls) => {
          for (const id of ids) {
            const url = urls[id];
            if (!url) continue;
            const img = new Image();
            img.src = url;
            await img.decode();
            portraits[id] = inkBust(img, INK);
          }
          paintAll();
        })
        .catch((e) => console.warn("mural portraits unavailable", e));
    });
  }
  return out;
}

// ---------------------------------------------------------------- lettering helpers
/** An enamel street plaque: cream plate, navy border, the French name over the Dutch. */
function paintStreet(g: G, W: number, H: number, text: string) {
  const [fr = "", nl = ""] = text.split("|");
  g.clearRect(0, 0, W, H);
  g.fillStyle = "#f3efe2";
  roundRect(g, 0, 0, W, H, H * 0.14);
  g.fill();
  g.lineWidth = H * 0.05;
  g.strokeStyle = ENAMEL;
  roundRect(g, H * 0.06, H * 0.06, W - H * 0.12, H - H * 0.12, H * 0.1);
  g.stroke();
  g.fillStyle = ENAMEL;
  g.textAlign = "center";
  g.textBaseline = "middle";
  const face = (px: number) => `700 ${px}px 'Big Shoulders Display', 'Arial Narrow', sans-serif`;
  fit(g, fr, face, W * 0.84, H * 0.36);
  g.fillText(fr, W / 2, H * 0.33);
  g.fillRect(W * 0.2, H * 0.5 - H * 0.012, W * 0.6, H * 0.024);
  fit(g, nl, face, W * 0.84, H * 0.3);
  g.fillText(nl, W / 2, H * 0.69);
}

/** An inscription cut into stone: letters in shadow with a lit lower edge. */
function paintLintel(g: G, W: number, H: number, text: string, bg: string) {
  g.fillStyle = bg;
  g.fillRect(0, 0, W, H);
  g.textAlign = "center";
  g.textBaseline = "middle";
  const px = fit(g, text, (p) => `700 ${p}px Cinzel, Georgia, serif`, W * 0.94, H * 0.72);
  const d = Math.max(1, px * 0.05);
  g.fillStyle = "rgba(255, 250, 236, 0.85)";
  g.fillText(text, W / 2 + d, H / 2 + d);
  g.fillStyle = "#6f644e";
  g.fillText(text, W / 2, H / 2);
}

/** Art-deco station lettering: gilded letters outlined in ink. */
function paintDeco(g: G, W: number, H: number, text: string, bg: string) {
  g.fillStyle = bg;
  g.fillRect(0, 0, W, H);
  g.textAlign = "center";
  g.textBaseline = "middle";
  const px = fit(g, text, (p) => `900 ${p}px 'Big Shoulders Display', 'Arial Narrow', sans-serif`, W * 0.96, H * 0.8);
  g.lineJoin = "round";
  g.lineWidth = px * 0.12;
  g.strokeStyle = INK;
  g.strokeText(text, W / 2, H / 2);
  g.fillStyle = "#d9a93a";
  g.fillText(text, W / 2, H / 2);
}

/** A clock face with Roman numerals, telling the player's own time. */
function paintClock(g: G, W: number, H: number) {
  const cx = W / 2;
  const cy = H / 2;
  const r = Math.min(W, H) / 2 - 2;
  g.clearRect(0, 0, W, H);
  g.fillStyle = INK;
  g.beginPath();
  g.arc(cx, cy, r, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = "#f7f1de";
  g.beginPath();
  g.arc(cx, cy, r * 0.9, 0, Math.PI * 2);
  g.fill();
  g.strokeStyle = INK;
  g.lineWidth = r * 0.02;
  g.beginPath();
  g.arc(cx, cy, r * 0.62, 0, Math.PI * 2);
  g.stroke();
  g.fillStyle = INK;
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.font = `700 ${r * 0.17}px Cinzel, Georgia, serif`;
  const numerals = ["XII", "I", "II", "III", "IIII", "V", "VI", "VII", "VIII", "IX", "X", "XI"];
  numerals.forEach((n, i) => {
    const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
    g.fillText(n, cx + Math.cos(a) * r * 0.76, cy + Math.sin(a) * r * 0.76);
  });
  const now = new Date();
  const hand = (a: number, len: number, w: number) => {
    g.lineWidth = w;
    g.lineCap = "round";
    g.beginPath();
    g.moveTo(cx - Math.cos(a) * len * 0.15, cy - Math.sin(a) * len * 0.15);
    g.lineTo(cx + Math.cos(a) * len, cy + Math.sin(a) * len);
    g.stroke();
  };
  const m = now.getMinutes() + now.getSeconds() / 60;
  const hr = (now.getHours() % 12) + m / 60;
  hand((hr / 12) * Math.PI * 2 - Math.PI / 2, r * 0.45, r * 0.07);
  hand((m / 60) * Math.PI * 2 - Math.PI / 2, r * 0.66, r * 0.045);
  g.fillStyle = "#d9a93a";
  g.beginPath();
  g.arc(cx, cy, r * 0.07, 0, Math.PI * 2);
  g.fill();
}

// ---------------------------------------------------------------- the murals
/**
 * A comic-strip mural: a flat-coloured panel with a sunburst behind a hero's bust, a caption box
 * with the hero's name, and a speech balloon (French over Dutch). The bust breaks the frame at the
 * bottom, the way the Brussels walls' figures step out of their panels. Until the bust is
 * rendered, a silhouette holds its place.
 */
function paintMural(g: G, W: number, H: number, ex: PanelExtras, hero: HeroId, bust?: HTMLCanvasElement) {
  const ground = ex.ground ?? "#e9c46a";
  const lw = W * 0.018;
  g.fillStyle = PAPER;
  g.fillRect(0, 0, W, H);
  const m = W * 0.05;
  const x0 = m;
  const y0 = m;
  const x1 = W - m;
  const y1 = H - m * 2.2;
  g.save();
  g.beginPath();
  g.rect(x0, y0, x1 - x0, y1 - y0);
  g.clip();
  g.fillStyle = ground;
  g.fillRect(x0, y0, x1 - x0, y1 - y0);
  // The sunburst behind the head.
  const cx = W / 2;
  const cy = y0 + (y1 - y0) * 0.5;
  g.fillStyle = mix(ground, "#ffffff", 0.3);
  const rays = 18;
  for (let i = 0; i < rays; i += 2) {
    const a0 = (i / rays) * Math.PI * 2;
    const a1 = ((i + 1) / rays) * Math.PI * 2;
    g.beginPath();
    g.moveTo(cx, cy);
    g.lineTo(cx + Math.cos(a0) * H * 1.5, cy + Math.sin(a0) * H * 1.5);
    g.lineTo(cx + Math.cos(a1) * H * 1.5, cy + Math.sin(a1) * H * 1.5);
    g.closePath();
    g.fill();
  }
  g.restore();
  g.lineWidth = lw * 1.4;
  g.strokeStyle = INK;
  g.strokeRect(x0, y0, x1 - x0, y1 - y0);
  // The hero, stepping out over the bottom of the frame.
  const s = W * 0.94;
  const bx = cx - s * 0.5;
  const by = y1 - s * 0.84;
  if (bust) g.drawImage(bust, bx, by, s, s);
  else {
    g.fillStyle = mix(ground, INK, 0.55);
    g.beginPath();
    g.ellipse(cx, by + s * 0.36, s * 0.16, s * 0.19, 0, 0, Math.PI * 2);
    g.fill();
    g.beginPath();
    g.ellipse(cx, by + s * 0.86, s * 0.42, s * 0.3, 0, Math.PI, 0);
    g.fill();
  }
  // Caption box.
  const def = HERO_LIST[hero]!;
  const name = def.name.toUpperCase();
  const face = (px: number) => `700 ${px}px 'Comic Neue', 'Comic Sans MS', sans-serif`;
  const capW = (x1 - x0) * 0.62;
  const capH = H * 0.06;
  g.fillStyle = "#fff6d8";
  g.fillRect(x0, y0, capW, capH);
  g.lineWidth = lw;
  g.strokeRect(x0, y0, capW, capH);
  g.fillStyle = INK;
  g.textAlign = "left";
  g.textBaseline = "middle";
  fit(g, name, face, capW * 0.9, capH * 0.62);
  g.fillText(name, x0 + capW * 0.05, y0 + capH * 0.54);
  // Speech balloon, its tail toward the hero's head.
  const [fr = "", nl = ""] = (ex.balloon ?? "").split("|");
  const bw = (x1 - x0) * 0.46;
  const bh = H * 0.13;
  const bcx = x1 - bw * 0.58;
  const bcy = y0 + capH + bh * 0.85;
  g.fillStyle = "#ffffff";
  g.strokeStyle = INK;
  g.lineWidth = lw;
  g.beginPath();
  g.moveTo(bcx - bw * 0.1, bcy + bh * 0.4);
  g.lineTo(cx + s * 0.08, by + s * 0.28);
  g.lineTo(bcx + bw * 0.08, bcy + bh * 0.42);
  g.closePath();
  g.fill();
  g.stroke();
  g.beginPath();
  g.ellipse(bcx, bcy, bw / 2, bh / 2, 0, 0, Math.PI * 2);
  g.fill();
  g.stroke();
  g.fillStyle = "#ffffff";
  g.beginPath();
  g.ellipse(bcx, bcy, bw / 2 - lw * 0.6, bh / 2 - lw * 0.6, 0, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = INK;
  g.textAlign = "center";
  fit(g, fr, face, bw * 0.78, bh * 0.36);
  g.fillText(fr, bcx, bcy - bh * 0.12);
  fit(g, nl, face, bw * 0.7, bh * 0.24);
  g.fillText(nl, bcx, bcy + bh * 0.2);
  // The painter's mark.
  g.font = face(H * 0.018);
  g.textAlign = "right";
  g.fillStyle = mix(PAPER, INK, 0.6);
  g.fillText("RIVALWATCH · BXL", W - m, H - m * 0.9);
}
